import React, { useCallback, useEffect, useState, type RefObject } from 'react';
import styled from 'styled-components';

/**
 * System sans-serif font stack used by overlays that don't simulate a
 * brand-specific typeface (Facebook, Instagram, Threads).
 */
const SYSTEM_FONT_STACK = `-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif`;

/**
 * Root container for an overlay. Fills the rect, sets `line-height: 1`
 * and white text color so chrome children can express dimensions in `em`
 * against the overlay's dynamic `fontPx` (derived from rect size).
 *
 * `$fontFamily` defaults to the system stack. Brand-typeface overlays
 * (LinkedIn / TikTok / YouTube Shorts) override it via the styled-prop.
 */
export const OverlayFrame = styled.div<{ $fontFamily?: string }>`
  position: relative;
  width: 100%;
  height: 100%;
  font-family: ${({ $fontFamily }) => $fontFamily ?? SYSTEM_FONT_STACK};
  line-height: 1;
  color: #ffffff;
`;

/**
 * Mute-toggle state. Local-only — the renderer doesn't expose a volume
 * API yet, so the speaker is visually accurate but doesn't change the
 * underlying audio. The hook's shape already matches a future bridge
 * wiring (`playback.setVolume`).
 *
 * Defaults to `muted = true` because every social platform we simulate
 * autoplays sound-off.
 */
export function useMuteToggle(initialMuted = true): {
  muted: boolean;
  toggle: () => void;
} {
  const [muted, setMuted] = useState(initialMuted);
  const toggle = useCallback(() => setMuted(m => !m), []);
  return { muted, toggle };
}

/**
 * Cursor-tracked hover for elements that live inside a
 * `pointer-events: none` parent — CSS `:hover` doesn't fire reliably in that
 * case, so we watch window `mousemove` against the element's bounding rect.
 *
 * Used by overlays where the root chrome stays click-through but individual
 * controls (like a play button) need a hover visual. Pass `enabled = false`
 * to skip the listener entirely (e.g. when the controls bar isn't painted).
 */
export function useCursorHover(
  ref: RefObject<HTMLElement | null>,
  enabled = true,
): boolean {
  const [hovered, setHovered] = useState(false);
  useEffect(() => {
    if (!enabled) {
      setHovered(prev => (prev ? false : prev));
      return;
    }
    const onMove = (e: MouseEvent) => {
      const el = ref.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const inside =
        e.clientX >= r.left &&
        e.clientX <= r.right &&
        e.clientY >= r.top &&
        e.clientY <= r.bottom;
      setHovered(prev => (prev === inside ? prev : inside));
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, [ref, enabled]);
  return hovered;
}

/**
 * Base mute-button visuals: button reset, absolute positioning, circular
 * dark backdrop, opacity dim-when-muted with a 150ms ease-out transition.
 *
 * Consumers extend via `styled(OverlayMuteButton)` to set `right`,
 * `bottom`, `width`, `height` — those vary per overlay because the
 * surrounding chrome lives at different heights. `$mutedOpacity` lets
 * an overlay choose how subtly the muted state reads (Facebook /
 * Instagram use 0.55 because the chrome around them is dense; Threads
 * uses 0.85 because the button is the only chrome).
 */
export const OverlayMuteButton = styled.button<{
  $muted: boolean;
  $mutedOpacity?: number;
}>`
  pointer-events: auto;
  position: absolute;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  border: none;
  padding: 0;
  cursor: pointer;
  font: inherit;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  opacity: ${({ $muted, $mutedOpacity = 0.55 }) => ($muted ? $mutedOpacity : 1)};
  transition: opacity 150ms ease-out;
`;

interface OverlayCaptionsBlockProps {
  text: string;
  /**
   * Whether the surrounding player-controls chrome is visible. Drives the
   * caption-block's vertical anchor — when controls are on, the block sits
   * above the bottom chrome row; when off, it sits at the rect bottom.
   * Mirrors how social-feed players reflow on auto-hide.
   */
  controlsOn: boolean;
  /** `bottom` offset (e.g. `'7.4em'`) used when `controlsOn` is true. */
  bottomWhenControlsOn: string;
  /** `bottom` offset (e.g. `'0.75em'`) used when `controlsOn` is false. */
  bottomWhenControlsOff: string;
}

/**
 * Centered caption block with per-line "clone" backgrounds — each wrapped
 * line gets its own tight black pill rather than one box around the whole
 * paragraph (via `box-decoration-break: clone`).
 *
 * Shared across Facebook and Instagram Feed Post overlays (LinkedIn Feed
 * Post uses a size-tiered variant that differs enough to stay separate).
 */
export const OverlayCaptionsBlock: React.FC<OverlayCaptionsBlockProps> = ({
  text,
  controlsOn,
  bottomWhenControlsOn,
  bottomWhenControlsOff,
}) => (
  <CaptionAnchor
    style={{ bottom: controlsOn ? bottomWhenControlsOn : bottomWhenControlsOff }}
  >
    <CaptionBox>
      <CaptionLines>{text}</CaptionLines>
    </CaptionBox>
  </CaptionAnchor>
);

const CaptionAnchor = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  pointer-events: none;
  padding: 0 0.4em;
  transition: bottom 200ms ease-out;
`;

const CaptionBox = styled.div`
  display: inline-block;
  color: #ffffff;
  font-size: 2.2em;
  font-weight: 500;
  letter-spacing: 0.005em;
  line-height: 1.35;
  text-align: center;
  max-width: 80%;
`;

const CaptionLines = styled.span`
  display: inline;
  background: rgba(0, 0, 0, 0.85);
  padding: 0.2em 0.4em;
  border-radius: 0.2em;
  box-decoration-break: clone;
  -webkit-box-decoration-break: clone;
`;
